/**
 * GoLiveReadinessBadge - Overall onboarding readiness indicator (DRR-184)
 *
 * Shows RED / YELLOW / GREEN status for facility go-live readiness.
 * Includes tooltip with blocking rings when not ready.
 *
 * Logic:
 * - RED if Ring 3 (WHY - regulatory compliance) incomplete
 * - YELLOW if any other ring incomplete
 * - GREEN if all rings complete
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - No domain-specific terminology
 */

import { Badge, Group, Loader, Stack, Text, Tooltip } from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconCircleCheck,
  IconCircleX,
} from '@tabler/icons-react';
import type { ReadinessLevel } from '@/hooks/useOnboardingReadiness';
import { READINESS_COLORS, READINESS_LABELS } from '@/hooks/useOnboardingReadiness';

export interface GoLiveReadinessBadgeProps {
  /** Overall readiness status */
  status: ReadinessLevel;
  /** List of blocking ring IDs (e.g., ['WHO', 'WHY']) */
  blockingRings: string[];
  /** Overall completion percentage */
  percent: number;
  /** Whether data is still loading */
  loading?: boolean;
  /** Size variant */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** Show detailed text (not just badge) */
  detailed?: boolean;
}

const STATUS_ICONS = {
  RED: IconCircleX,
  YELLOW: IconAlertTriangle,
  GREEN: IconCircleCheck,
};

const STATUS_LABELS = {
  RED: 'BLOCKED',
  YELLOW: 'IN PROGRESS',
  GREEN: 'GO LIVE READY',
};

/**
 * Overall go-live readiness badge with tooltip showing blockers
 *
 * @example
 * const { overallStatus, blockingRings, overallPercent } = useOnboardingReadiness();
 * <GoLiveReadinessBadge
 *   status={overallStatus}
 *   blockingRings={blockingRings}
 *   percent={overallPercent}
 * />
 */
export function GoLiveReadinessBadge({
  status,
  blockingRings,
  percent,
  loading = false,
  size = 'lg',
  detailed = false,
}: GoLiveReadinessBadgeProps) {
  const Icon = STATUS_ICONS[status];
  const color = READINESS_COLORS[status];
  const label = STATUS_LABELS[status];

  if (loading) {
    return (
      <Badge variant="light" color="gray" size={size} leftSection={<Loader size={12} />}>
        Calculating...
      </Badge>
    );
  }

  const badgeContent = (
    <Badge
      variant={status === 'GREEN' ? 'filled' : 'light'}
      color={color}
      size={size}
      leftSection={<Icon size={size === 'lg' ? 16 : size === 'xl' ? 18 : 14} />}
    >
      {label}
    </Badge>
  );

  // Tooltip content showing blockers
  const tooltipContent = (
    <Stack gap={4}>
      <Group gap={4}>
        <Text size="xs" fw={600}>
          {percent}% Complete
        </Text>
      </Group>
      {blockingRings.length > 0 ? (
        <>
          <Text size="xs" c="dimmed">Blocking dimensions:</Text>
          <Group gap={4}>
            {blockingRings.map((ring) => (
              <Badge key={ring} size="xs" variant="outline" color={ring === 'WHY' ? 'red' : 'yellow'}>
                {ring}
              </Badge>
            ))}
          </Group>
          {blockingRings.includes('WHY') && (
            <Text size="xs" c="red.4" fw={500}>
              WHY (regulatory) must complete first
            </Text>
          )}
        </>
      ) : (
        <Group gap={4}>
          <IconCheck size={12} color="green" />
          <Text size="xs" c="green.4">All dimensions complete</Text>
        </Group>
      )}
    </Stack>
  );

  if (detailed) {
    return (
      <Group gap="sm">
        <Tooltip label={tooltipContent} multiline w={200}>
          {badgeContent}
        </Tooltip>
        <Text size="sm" c="dimmed">
          {percent}% complete
        </Text>
        {blockingRings.length > 0 && (
          <Text size="sm" c={status === 'RED' ? 'red' : 'yellow'}>
            ({blockingRings.length} dimension{blockingRings.length !== 1 ? 's' : ''} blocking)
          </Text>
        )}
      </Group>
    );
  }

  return (
    <Tooltip label={tooltipContent} multiline w={200}>
      {badgeContent}
    </Tooltip>
  );
}

export default GoLiveReadinessBadge;
